import React, { useState, useRef } from 'react';
import api from '../api/client';

interface Category {
  name: string;
  supply: number;
  activityPoints: number;
  startingPrice: number;
  description: string;
}

interface Cap {
  name: string;
  limit: number;
  coefficients: number[]; // positional — index i corresponds to categories[i]
}

interface Props {
  onCreated: () => void;
}

export default function CreateAuctionForm({ onCreated }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rho, setRho] = useState(1.0);
  const [priceIncrement, setPriceIncrement] = useState(0);
  const [activityTolerance, setActivityTolerance] = useState(2);
  const [categories, setCategories] = useState<Category[]>([
    { name: '', supply: 0, activityPoints: 1, startingPrice: 0, description: '' },
    { name: '', supply: 0, activityPoints: 1, startingPrice: 0, description: '' },
  ]);
  const [caps, setCaps] = useState<Cap[]>([]);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleLoadFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target?.result as string);
        if (data.title !== undefined)
          setTitle(String(data.title));
        if (data.description !== undefined)
          setDescription(String(data.description ?? ''));
        if (data.rho !== undefined) {
          const v = parseFloat(data.rho);
          if (!isNaN(v)) setRho(v);
        }
        if (data.priceIncrement !== undefined) {
          const v = parseFloat(data.priceIncrement);
          if (!isNaN(v)) setPriceIncrement(v);
        }
        if (data.activityTolerance !== undefined) {
          const v = parseInt(data.activityTolerance);
          if (!isNaN(v)) setActivityTolerance(v);
        }
        let loadedCatNames: string[] = [];
        if (Array.isArray(data.categories)) {
          loadedCatNames = data.categories.map((c: any) => String(c.name ?? ''));
          setCategories(data.categories.map((c: any) => ({
            name:           String(c.name ?? ''),
            supply:         Math.max(0, parseInt(c.supply) || 0),
            activityPoints: Math.max(1, parseInt(c.activityPoints) || 1),
            startingPrice:  Math.max(0, parseFloat(c.startingPrice) || 0),
            description:    String(c.description ?? ''),
          })));
        }
        let capError = '';
        if (Array.isArray(data.caps)) {
          const catNameSet = new Set(loadedCatNames);
          const unmatched: string[] = [];
          for (const cap of data.caps) {
            for (const term of (cap.terms ?? [])) {
              if (term.categoryName && !catNameSet.has(String(term.categoryName))) {
                unmatched.push(`"${term.categoryName}" in cap "${cap.name}"`);
              }
            }
          }
          if (unmatched.length > 0) {
            capError = `Caps not imported — unmatched category names: ${unmatched.join(', ')}`;
            setCaps([]);
          } else {
            setCaps(data.caps.map((cap: any) => ({
              name: String(cap.name ?? ''),
              limit: Math.max(0, parseFloat(cap.limit) || 0),
              coefficients: loadedCatNames.map((catName: string) => {
                const term = (cap.terms ?? []).find((t: any) => t.categoryName === catName);
                return term ? parseFloat(term.coefficient) || 0 : 0;
              }),
            })));
          }
        } else {
          setCaps([]);
        }
        setError(capError);
      } catch {
        setError('Could not read file — make sure it is valid JSON.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  function updateCategory(index: number, field: keyof Category, value: string | number) {
    setCategories(cats => cats.map((c, i) => i === index ? { ...c, [field]: value } : c));
  }

  function addCategory() {
    setCategories(cats => [...cats, { name: '', supply: 0, activityPoints: 1, startingPrice: 0, description: '' }]);
    setCaps(c => c.map(cap => ({ ...cap, coefficients: [...cap.coefficients, 0] })));
  }

  function removeCategory(index: number) {
    setCategories(cats => cats.filter((_, i) => i !== index));
    setCaps(c => c.map(cap => ({ ...cap, coefficients: cap.coefficients.filter((_, i) => i !== index) })));
  }

  function addCap() {
    setCaps(c => [...c, { name: '', limit: 0, coefficients: categories.map(() => 0) }]);
  }

  function removeCap(index: number) {
    setCaps(c => c.filter((_, i) => i !== index));
  }

  function updateCap(capIdx: number, field: 'name' | 'limit', value: string | number) {
    setCaps(c => c.map((cap, i) => i === capIdx ? { ...cap, [field]: value } : cap));
  }

  function updateCapCoeff(capIdx: number, catIdx: number, value: number) {
    setCaps(c => c.map((cap, i) => {
      if (i !== capIdx) return cap;
      const newCoeffs = [...cap.coefficients];
      newCoeffs[catIdx] = value;
      return { ...cap, coefficients: newCoeffs };
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      await api.post('/auctions', {
        title, description, rho, priceIncrement, activityTolerance, categories,
        caps: caps.filter(cap => cap.name).map(cap => ({
          name: cap.name,
          limit: cap.limit,
          coefficients: cap.coefficients,
        })),
      });
      onCreated();
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to create auction');
    }
  }

  return (
    <div style={styles.container}>
      <h3>Create New Auction</h3>
      <form onSubmit={handleSubmit} style={styles.form}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleLoadFile}
        />
        <button type="button" style={styles.loadBtn} onClick={() => fileInputRef.current?.click()}>
          Load from file
        </button>
        <label style={styles.label}>Title</label>
        <input data-testid="auction-title" style={styles.input} value={title} onChange={e => setTitle(e.target.value)} required />

        <label style={styles.label}>Description</label>
        <textarea data-testid="auction-description" style={styles.input} value={description} onChange={e => setDescription(e.target.value)} rows={2} />

        <label style={styles.label}>Activity Requirement (rho) — typically 0.9–1.0</label>
        <input data-testid="auction-rho" style={styles.input} type="number" step="0.01" min="0.1" max="1" value={rho}
          onChange={e => setRho(parseFloat(e.target.value))} required />

        <label style={styles.label}>Price Increment (%) — applied to categories with excess demand each round</label>
        <input style={styles.input} type="number" step="1" min="0" value={priceIncrement}
          onChange={e => setPriceIncrement(parseFloat(e.target.value) || 0)} required />

        <label style={styles.label}>
          Activity Tolerance (eligibility points) — allowance above eligibility from round 2
          onward; 2 under the published rules, 0 for the simplified ruleset
        </label>
        <input data-testid="auction-activity-tolerance" style={styles.input} type="number" step="1" min="0" max="10" value={activityTolerance}
          onChange={e => setActivityTolerance(parseInt(e.target.value) || 0)} required />

        <h4>Categories (Lots)</h4>
        {categories.map((cat, i) => (
          <div key={i} style={styles.categoryBox}>
            <div style={styles.categoryHeader}>
              <strong>Category {i + 1}</strong>
              {categories.length > 1 && (
                <button type="button" style={styles.removeBtn} onClick={() => removeCategory(i)}>Remove</button>
              )}
            </div>
            <label style={styles.label}>Name</label>
            <input data-testid={`cat-name-${i}`} style={styles.input} value={cat.name} onChange={e => updateCategory(i, 'name', e.target.value)} required />
            <label style={styles.label}>Supply (units available)</label>
            <input data-testid={`cat-supply-${i}`} style={styles.input} type="number" min="1" value={cat.supply}
              onChange={e => updateCategory(i, 'supply', parseInt(e.target.value))} required />
            <label style={styles.label}>Activity Points per unit</label>
            <input data-testid={`cat-activity-${i}`} style={styles.input} type="number" min="1" value={cat.activityPoints}
              onChange={e => updateCategory(i, 'activityPoints', parseInt(e.target.value))} required />
            <label style={styles.label}>Starting Price per Unit</label>
            <input style={styles.input} type="number" min="0" step="1" value={cat.startingPrice}
              onChange={e => updateCategory(i, 'startingPrice', parseFloat(e.target.value) || 0)} required />
            <label style={styles.label}>Description (optional)</label>
            <input style={styles.input} value={cat.description}
              onChange={e => updateCategory(i, 'description', e.target.value)} />
          </div>
        ))}

        <button type="button" style={styles.addBtn} onClick={addCategory}>+ Add Category</button>

        <h4 style={{ marginTop: '1rem' }}>Caps (optional)</h4>
        {caps.map((cap, ci) => (
          <div key={ci} style={{ ...styles.categoryBox, borderColor: '#c8e6c9' }}>
            <div style={styles.categoryHeader}>
              <strong>Cap {ci + 1}</strong>
              <button type="button" style={styles.removeBtn} onClick={() => removeCap(ci)}>Remove</button>
            </div>
            <label style={styles.label}>Cap Name</label>
            <input style={styles.input} value={cap.name}
              onChange={e => updateCap(ci, 'name', e.target.value)} required />
            <label style={styles.label}>Limit</label>
            <input style={styles.input} type="number" min="0" step="any" value={cap.limit}
              onChange={e => updateCap(ci, 'limit', parseFloat(e.target.value) || 0)} required />
            <label style={{ ...styles.label, marginTop: '0.25rem' }}>Coefficients (per category)</label>
            {categories.map((cat, catIdx) => (
              <div key={catIdx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ ...styles.label, minWidth: '120px', flexShrink: 0 }}>
                  {cat.name || `Category ${catIdx + 1}`}
                </span>
                <input
                  style={{ ...styles.input, width: '90px' }}
                  type="number" min="0" step="any"
                  value={cap.coefficients[catIdx] ?? 0}
                  onChange={e => updateCapCoeff(ci, catIdx, parseFloat(e.target.value) || 0)}
                />
              </div>
            ))}
          </div>
        ))}
        <button type="button" style={{ ...styles.addBtn, borderColor: '#a5d6a7', color: '#2e7d32', background: '#e8f5e9' }} onClick={addCap}>+ Add Cap</button>

        {error && <p style={styles.error}>{error}</p>}
        <button type="submit" style={styles.submitBtn}>Create Auction</button>
      </form>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)', maxWidth: '600px' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  label: { fontSize: '0.875rem', fontWeight: 500, color: '#444' },
  input: { padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '1rem', width: '100%', boxSizing: 'border-box' },
  categoryBox: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '1rem', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' },
  removeBtn: { padding: '0.25rem 0.5rem', background: '#fce8e6', color: '#c62828', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' },
  addBtn: { padding: '0.5rem', background: '#e8eaf6', color: '#3949ab', border: 'none', borderRadius: '4px', cursor: 'pointer', marginTop: '0.5rem' },
  submitBtn: { padding: '0.75rem', background: '#4a90e2', color: 'white', border: 'none', borderRadius: '4px', fontSize: '1rem', cursor: 'pointer', marginTop: '0.5rem' },
  error: { color: 'red', fontSize: '0.875rem' },
  loadBtn: { padding: '0.4rem 0.8rem', background: '#f5f5f5', color: '#333', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '0.875rem', alignSelf: 'flex-start', marginBottom: '0.5rem' },
};
